import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './QuizScreen.css';

function QuizScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Log the entire location state for debugging
  console.log("QuizScreen received location.state:", location.state);
  
  const { match, multiplier, amount } = location.state || {};
  
  // Log the extracted values
  console.log("QuizScreen extracted values:", { match, multiplier, amount });
  
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(120); // 120 seconds for entire quiz
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleTimeOut = useCallback(() => {
    // Select random answers for any unanswered questions
    const newAnswers = [...answers];
    for (let i = 0; i < questions.length; i++) {
      if (newAnswers[i] === undefined) {
        newAnswers[i] = Math.floor(Math.random() * questions[i].options.length);
      }
    }
    setAnswers(newAnswers);
    setQuizCompleted(true);
    
    // Ensure multiplier and amount are passed correctly
    console.log("QuizScreen handleTimeOut - passing values:", { 
      multiplier, 
      amount,
      matchId: match?._id
    });
    
    // Navigate to preview page with all answers and timeout flag
    navigate('/preview', {
      state: {
        answers: newAnswers,
        questions,
        match,
        isTimeout: true,
        amount: amount || 0,
        multiplier: multiplier || 1,
      }
    });
  }, [answers, questions, match, multiplier, amount, navigate]);

  // Validate match data
  useEffect(() => {
    if (!match) {
      setError('No match data available. Please select a match first.');
      setLoading(false);
      return;
    }

    if (!match._id) {
      setError('Invalid match data. Please try again.');
      setLoading(false);
      return;
    }

    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/match-setup/${match._id}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch questions');
        }

        const data = await response.json();
        if (data.success && data.data.questions) {
          setQuestions(data.data.questions);
        } else {
          throw new Error('No questions found for this match');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching questions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [match]);

  useEffect(() => {
    // If we have persisted answers, set the selected option for the current question
    if (answers[currentQuestionIndex] !== undefined) {
      setSelectedOption(answers[currentQuestionIndex]);
    }
  }, [currentQuestionIndex, answers]);

  // Timer effect for entire quiz
  useEffect(() => {
    let timer;
    
    if (timeLeft > 0 && !quizCompleted && questions.length > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeOut();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Cleanup function
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [timeLeft, quizCompleted, questions, handleTimeOut]);

  const handleOptionSelect = (optionIndex) => {
    setSelectedOption(optionIndex);
    // Update the answers array immediately
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = optionIndex;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (selectedOption === null) return;

    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = selectedOption;
    setAnswers(newAnswers);
    setSelectedOption(null);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setQuizCompleted(true);
      
      // Ensure multiplier and amount are passed correctly
      console.log("QuizScreen handleNext - passing values:", { 
        multiplier, 
        amount,
        matchId: match?._id
      });
      
      // Quiz completed, navigate to preview page
      navigate('/preview', {
        state: {
          answers: newAnswers,
          questions,
          match,
          amount: amount || 0,
          multiplier: multiplier || 1
        }
      });
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setSelectedOption(answers[currentQuestionIndex - 1]);
    }
  };

  if (loading) {
    return (
      <div className="quiz-screen">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-screen">
        <div className="error-message">
          <h2>Error: {error}</h2>
          <p>Please go back and try again.</p>
          <button 
            className="back-button"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!match || !match._id) {
    return (
      <div className="quiz-screen">
        <div className="error-message">
          <h2>Invalid Match</h2>
          <p>Please select a valid match to start the quiz.</p>
          <button 
            className="back-button"
            onClick={() => navigate('/')}
          >
            Return to Matches
          </button>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="quiz-screen">
        <div className="error-message">
          <h2>No Questions Available</h2>
          <p>There are no questions available for this match yet.</p>
          <button 
            className="back-button"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  // const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="quiz-screen">
      <div className="quiz-container">
        <div className="back-button-container">
          {/* <button onClick={() => navigate(-1)} className="back-nav">
            <span>←</span>
          </button> */}
        </div>
        {currentQuestionIndex === 0 && (
          <div className="terms-condition" style={{marginTop:'-40px'}}>
            <p>
            Ensure you complete the quiz within the time limit. If you go back or exit the quiz early, your answers will be automatically submitted.
            </p>
          </div>
        )}

        
        <div className="teams-header">
          <h2>{match.team1} <span className="vs-text">vs</span> {match.team2}</h2>
        </div>
        
        <div className="current-question">
          <span className="question-number-circle">{currentQuestionIndex + 1}</span>
        </div>
        
        <div className="progress-indicator">
          {[...Array(questions.length)].map((_, index) => (
            <div 
              key={index} 
              className={`progress-dot ${index <= currentQuestionIndex ? 'active' : ''}`}
            />
          ))}
        </div>

        <div className="question-container">
          <h2 className="question-text">
            {currentQuestion.question}
          </h2>
        </div>

        <div className="options-container">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              className={`option-button ${selectedOption === index ? 'selected' : ''}`}
              onClick={() => handleOptionSelect(index)}
            >
              <div className="option-circle">
                <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              </div>
              <span className="option-text">{option}</span>
            </button>
          ))}
        </div>

        <div className="timer-section">
          <div className="timer-circle-container">
            <svg className="timer-svg" viewBox="0 0 100 100">
              <circle
                className="timer-background"
                cx="50"
                cy="50"
                r="45"
              />
              <circle
                className="timer-progress"
                cx="50"
                cy="50"
                r="45"
                style={{
                  strokeDashoffset: `${283 - (283 * timeLeft) / 120}`
                }}
              />
            </svg>
            <span className="timer-text">{timeLeft}s</span>
          </div>
        </div>

        <div className="action-container">
          {currentQuestionIndex > 0 && (
            <button 
              className="previous-button"
              onClick={handlePrevious}
            >
              Previous
            </button>
          )}
          <button 
            className="next-button"
            onClick={handleNext}
            disabled={selectedOption === null}
          >
            {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuizScreen; 